/**
 * ImageClickOverlay — image with interactive click-to-mark overlay.
 *
 * @remarks
 * Purely presentational. Renders the capture image, captures click
 * coordinates (converted to natural pixel space), and optionally shows a
 * circular pin marker at the previously marked position.
 *
 * Crosshair cursor is applied via `.sv-img-overlay--marking` when
 * `isMarking` is true.
 *
 * Pin position uses percentage values derived from natural dimensions so
 * it stays correct regardless of the rendered size.
 */

import { useRef, useState } from 'react';

interface ImageClickOverlayProps {
  src: string;
  alt: string;
  isMarking: boolean;
  markedImageX: number | null;
  markedImageY: number | null;
  /** Called with natural-pixel coordinates + naturalWidth for position calc. */
  onImageClick: (imageX: number, imageY: number, naturalWidth: number) => void;
}

export function ImageClickOverlay({
  src,
  alt,
  isMarking,
  markedImageX,
  markedImageY,
  onImageClick,
}: ImageClickOverlayProps): JSX.Element {
  const imgRef = useRef<HTMLImageElement>(null);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);

  function handleLoad(): void {
    const img = imgRef.current;
    if (img) setNatural({ w: img.naturalWidth, h: img.naturalHeight });
  }

  function handleClick(e: React.MouseEvent<HTMLImageElement>): void {
    if (!isMarking || !natural || natural.w === 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const imageX = Math.round(((e.clientX - rect.left) / rect.width) * natural.w);
    const imageY = Math.round(((e.clientY - rect.top) / rect.height) * natural.h);
    onImageClick(imageX, imageY, natural.w);
  }

  return (
    <div className={`sv-img-overlay${isMarking ? ' sv-img-overlay--marking' : ''}`}>
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        className="sv-popup-img"
        draggable={false}
        onLoad={handleLoad}
        onClick={handleClick}
      />
      {markedImageX !== null && markedImageY !== null && natural !== null && natural.w > 0 && (
        <div
          className="sv-img-marker"
          aria-hidden
          style={{
            left: `${(markedImageX / natural.w) * 100}%`,
            top: `${(markedImageY / natural.h) * 100}%`,
          }}
        />
      )}
    </div>
  );
}
